import React from 'react';
import styled from 'styled-components/macro';
import { assignUnassignActions } from 'store/actions';
import MainFiles from 'components/main/MainFiles';
import BookingNoInput from 'components/main/BookingNoInput';
import {
  Avatar,
  Box,
  Chip,
  FormHelperText,
  Tooltip,
} from '@material-ui/core';

import SiCheckbox from 'components/main/SiCheckbox';
import { Assignee } from 'store/models/Assignees';
import PreviewEmailButton from 'components/main/PreviewEmailButton';
import common from 'contexts/theme/common';
import PreviewBodyHtmlLinkIframe from 'components/main/PreviewBodyHtmlLinkIframe';
import { MainBookings, MatUiCheckbox } from 'components/main';
import { SectionI } from 'form-defs';
import { urgencies } from 'store/models/Urgency';
import { siKinds } from 'store/models/SiKind';
import DurationPanel from '../components/common/DurationPanel';
export const leftSection: SectionI[] = [
  {
    title: '',
    fields: [
      {
        name: 'timeTracking',
        label: 'Time Tracking',
        type: 'CUSTOM',
        renderWithArgs: ({
          userIsActive,
          setUserIsActive,
          formTicket,
          buttonClick,
          setButtonClick,
          hasChangeOrSaveClickedRef,
          handleAssignToMe,
        }: {
          userIsActive: boolean;
          setUserIsActive: Function;
          formTicket: any;
          buttonClick: string | undefined;
          setButtonClick: Function;
          hasChangeOrSaveClickedRef: {
            current: boolean;
          };
          handleAssignToMe: Function;
        }) => {
          return (
            <STimeSpentAndAssignToMe>
              <DurationPanel
                hasChangeOrSaveClickedRef={hasChangeOrSaveClickedRef}
                setButtonClick={setButtonClick}
                buttonClick={buttonClick}
                userIsActive={userIsActive}
                setUserIsActive={setUserIsActive}
                formTicket={formTicket}
              />
              {formTicket?.ticket?.assignee?.name.toLowerCase() ===
                'system' &&
                (formTicket?.ticket?.status === 'SUCCESS' ||
                  formTicket?.ticket?.status === 'FAILED') && (
                  <SChip
                    avatar={<Avatar />}
                    clickable
                    label="Assign to me"
                    color="primary"
                    onClick={() => handleAssignToMe()}
                  />
                )}
            </STimeSpentAndAssignToMe>
          );
        },
      },
    ],
  },
  {
    title: 'Tag Information',
    fields: [
      {
        name: 'siAndOthers',
        type: 'CUSTOM',
        renderWithArgs: (props) => {
          console.debug(`props:`, props);
          const {
            formTicket,
            canEdit,
            dispatch,
            canMarkSi,
            canAssign,
            setButtonClick,
          } = props;
          if (!formTicket) return null;
          const { ticket } = formTicket;
          const assigneeM = new Assignee(
            formTicket?.ticket?.assignee,
          );
          const hasAssignee = !!formTicket?.ticket?.assignee;
          return (
            <SSiAndOthers>
              <SiCheckbox
                ticketId={formTicket.id}
                value={ticket?.si || false} // from api
                disabled={!canMarkSi}
              />
              <SStatus>
                <Chip variant="outlined" label={ticket?.status} />
              </SStatus>
              <SAssignee>
                <Tooltip
                  title={
                    hasAssignee
                      ? 'Click to unassign'
                      : 'Click to assign'
                  }
                >
                  <SChip
                    clickable
                    disabled={(!canEdit && hasAssignee) || !canAssign}
                    avatar={
                      <Avatar src={assigneeM?.imageUrl}>
                        {assigneeM?.firstLetter()}
                      </Avatar>
                    }
                    label={assigneeM?.displayName('Unassigned')}
                    onClick={() => {
                      if (hasAssignee) {
                        setButtonClick('UNASSIGNED');
                      } else {
                        dispatch(
                          assignUnassignActions.postAssignTicket(
                            formTicket.id,
                          ),
                        );
                      }
                    }}
                  />
                </Tooltip>
              </SAssignee>
            </SSiAndOthers>
          );
        },
      },
      {
        name: 'bookingNo',
        label: 'Booking No.',
        type: 'CUSTOM',
        renderWithArgs: ({
          field,
          handleValueChanged,
          handleOnBlur,
          canEdit,
          formTicket,
          isMultiBooking,
          mainBookingM,
          handleOnFocus,
        }) => {
          return (
            <BookingNoInput
              index={-1} // index affects  position where the info popup shown and loading onclick info button
              verticalDir="bottom"
              field={field}
              handleValueChanged={handleValueChanged}
              bookingM={mainBookingM}
              handleOnBlur={handleOnBlur}
              disabled={!canEdit}
              handleOnFocus={handleOnFocus}
              hideInfoIcon={isMultiBooking}
              showValAsTransparentOnBlur={isMultiBooking}
            />
          );
        },
      },
      {
        name: 'urgency',
        recommendedValueField: 'recommendedUrgency',
        label: 'Urgency',
        type: 'DROPDOWN',
        options: urgencies,
        defaultValueField: 'defaultUrgency',
      },
      {
        name: 'siKind',
        recommendedValueField: 'recommendedSiKind',
        defaultValueField: 'defaultSiKind',
        label: 'S/I Kind',
        type: 'DROPDOWN',
        options: siKinds,
      },
    ],
  },
  {
    title: 'Additional Tag Information',
    fields: [
      {
        name: 'bookingSetting', // no label
        type: 'SUB_FIELDS', // has sub fields
        fields: [
          {
            name: 'isSplit',
            type: 'CUSTOM',
            label: 'Split Booking',
            renderWithArgs: ({
              value,
              field,
              handleValueChanged,
              canEdit,
              autoMarkIsSplitError,
            }) => {
              return (
                // need space in case there is split error
                <Box
                  position="relative"
                  marginBottom={
                    autoMarkIsSplitError ? '0.5rem' : undefined
                  }
                >
                  <MatUiCheckbox
                    label={field.label}
                    name={field.name}
                    value={value ?? false}
                    handleChange={(checked) =>
                      handleValueChanged(checked, field.name)
                    }
                    disabled={!canEdit || field.disabled}
                  />
                  {/* absolute so it doesnt push the booking cound to the right */}
                  {autoMarkIsSplitError && (
                    <FormHelperText
                      error
                      style={{
                        position: 'absolute',
                        bottom: '-20px',
                        width: '300px',
                      }}
                    >
                      {autoMarkIsSplitError}
                    </FormHelperText>
                  )}
                </Box>
              );
            },
          },
          {
            name: 'bookingCount',
            type: 'CUSTOM',
            renderWithArgs: ({ bookingsCount }) => {
              return (
                bookingsCount > 0 && (
                  <SBookingsCount>
                    <Chip
                      color="primary"
                      size="small"
                      variant="outlined"
                      label={
                        <SSmallText>{`Booking${
                          bookingsCount > 1 ? 's' : ''
                        }`}</SSmallText>
                      }
                      avatar={
                        <Avatar
                          style={{
                            background: common.palette.primary.main,
                            color:
                              common.palette.primary.contrastText,
                          }}
                        >
                          {bookingsCount}
                        </Avatar>
                      }
                    />
                  </SBookingsCount>
                )
              );
            },
          },
        ],
      },
      {
        name: 'destinationEmail',
        label: 'OCPS Destination Email',
        type: 'TEXT',
        isMultiRow: true,
        disabled: true, // disabled editing
        placeholder: '-',
      },
      {
        name: 'senderId',
        label: 'Indexer ID',
        type: 'TEXT',
        isMultiRow: true,
        disabled: true, // disabled editing
        placeholder: '-',
      },
      {
        name: 'customerEmailAddr',
        label: 'Customer Email Address',
        type: 'TEXT',
        placeholder: 'Please enter customer email address',
        disabled: false,
      },
    ],
  },
  {
    title: 'Comment to Doc. Staff',
    fields: [
      {
        name: 'commentToDoc',
        type: 'TEXT',
        // no label
        placeholder: 'Please enter comment to doc. staff',
        isMultiRow: true,
      },
    ],
  },
];

export const rightSection: SectionI[] = [
  {
    // no title
    fields: [
      {
        name: 'fromEmailTo',
        label: 'TO',
        type: 'TEXT',
        disabled: true, // disabled editing
        labelOnLeft: true,
      },
      {
        name: 'fromEmailFrom',
        label: 'FROM',
        type: 'TEXT',
        disabled: true, // disabled editing
        labelOnLeft: true,
      },
      {
        name: 'fromEmailSubject',
        label: 'SUBJ',
        type: 'TEXT',
        labelOnLeft: true,
        disabled: true, // disabled editing
      },
      {
        name: 'bodyHtmlLink',
        type: 'CUSTOM',
        renderWithArgs: ({
          value,
          dispatch,
          isLoading,
          formTicket,
        }) => {
          return (
            <SBodyHtmlWrapper>
              <PreviewBodyHtmlLinkIframe
                ticketId={formTicket?.id}
                bodyHtmlLink={formTicket?.bodyHtmlLink}
              />
            </SBodyHtmlWrapper>
          );
        },
      },
      {
        name: 'bodyPreviewUrl',
        loadingName: 'bodyPreviewUrlIsLoading',
        type: 'CUSTOM',
        renderWithArgs: ({ value, isLoading }) => {
          return (
            <PreviewEmailButton
              bodyPreviewUrl={value}
              isLoading={isLoading}
            />
          );
        },
      },
    ],
  },
  {
    title: 'Files Attached in the Email',
    hideTitle: true,
    fields: [
      {
        name: 'files',
        type: 'CUSTOM',
        renderWithArgs: ({ hasChangeOrSaveClickedRef }) => {
          return (
            <MainFiles
              hasChangeOrSaveClickedRef={hasChangeOrSaveClickedRef}
              isAvailableFiles={true}
              title="Files Attached in the Email"
              supTitle="
          Drag and drop your files here or use the “Add Files” button"
            />
          );
        },
      },
    ],
  },
  {
    title: 'Files Stored in Recycle Bin',
    hideTitle: true,
    fields: [
      {
        name: 'recycleBin',
        type: 'CUSTOM',
        renderWithArgs: ({ hasChangeOrSaveClickedRef }) => {
          return (
            <MainFiles
              hasChangeOrSaveClickedRef={hasChangeOrSaveClickedRef}
              isAvailableFiles={false}
              title="Files Stored in Recycle Bin"
              supTitle="Select files and click 'Restore' button"
            />
          );
        },
      },
    ],
  },
  {
    title: 'Bookings',
    fields: [
      {
        name: 'bookings',
        type: 'CUSTOM',
        renderWithoutArgs: () => {
          return <MainBookings />;
        },
      },
    ],
  },
];

export const mainPageFormDef = {
  leftSection,
  rightSection,
};
const STimeSpentAndAssignToMe = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SBodyHtmlWrapper = styled.div`
  position: relative;
  height: 30vh;
  overflow: auto;
  width: 66vw;
  border: solid 1px
    ${(props) => props.theme.palette.common.borderGrey};
  border-radius: ${(props) => props.theme.shape.borderRadius};
  display: flex;
`;
const SSiAndOthers = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const SStatus = styled.div``;
const SAssignee = styled.div``;

const SChip = styled(Chip)`
  &.MuiChip-root:not(.Mui-disabled) {
    /* add box shadow to show that it's clickable */
    box-shadow: 0 3px 6px rgb(0 0 0 / 16%), 0 1px 2px rgb(0 0 0 / 23%);
  }
`;

const SBookingsCount = styled.div``;
const SSmallText = styled.div`
  font-size: ${(props) => props.theme.typography.fontSize.small};
`;
